import React, { useState, useEffect } from 'react';
import ClientHeader from './ClientHeader';
import ClientSidebar from './ClientSidebar';
import Mailbox from '../../../components/Mailbox/Mailbox';
import PublicChat from '../../../components/PublicChat/PublicChat';
import EntrepreneurDashboard from '../../../components/entrepreneurProfile/EntrepreneurDashboard';
import ClientsCRM from '../../../components/crm/ClientsCRM';
import IntroToCostingAndManAcc from '../../../components/training/IntroToCostingAndManAcc';
import DesignABusiness from '../../../components/training/DesignABusiness';
import GrantBasics from '../../../components/Funding/GrantBasics';
import ApplicationTutorials from '../../../components/Funding/ApplicationTutorials';
import LogoutConfirmation from '../../../components/ui/LogoutConfirmation';
import { useAuth } from '../../../context/AuthContext';
import ScalingModules from '../../../components/Modules/ScalingModules';
import EntrepreneurProfilingDashboard from '../../../components/ProfilingForms/EntrepreneurProfilingDashboard';
import { doc, getDoc } from 'firebase/firestore';
import { firestore } from '../../../database/firebase';
import HealthDashboard from '../../../components/HealthCheck/HealthDashboard';
import DocumentBankPage from '../../../components/DocumentBank/DocumentBankPage';
import SettingsPageComponent from '../../main/SettingsPage';
import Commercialisation from '../../../components/Commercialisation/Commercialisation';
import CommercialisationReadinessChecklist from '../../../components/Commercialisation/CommercialisationReadinessChecklist';
import GTMStrategyPlan from '../../../components/Commercialisation/GTMStrategyPlan';
import StartupLaunchChecklist from '../../../components/Commercialisation/StartupLaunchChecklist';
import ProductDevelopment from '../../../components/ProductDevelopment/ProductDevelopment';
import {
  CoachingTools,
  BestPractices,
  KnowledgeBase,
  Templates,
} from '../../../components/coaches/resources';
import TeamMembersManagerV2 from '../../../components/team/TeamMembersManagerV2';

const ClientTraining = () => {
  const [activeCourse, setActiveCourse] = useState('business');
  const [userRole] = useState('client');

  return (
    <div className="min-h-screen bg-white">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="mb-2 text-3xl font-bold text-black">Training Programs</h1>
          <p className="text-gray-600">
            Access your assigned training modules and track your progress
          </p>
        </div>

        <div className="mb-8 rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveCourse('business')}
                className={`flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition-colors ${
                  activeCourse === 'business'
                    ? 'border-blue-600 text-black'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-black'
                }`}
              >
                <span>Design-A-Business</span>
              </button>
              <button
                onClick={() => setActiveCourse('costing')}
                className={`flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition-colors ${
                  activeCourse === 'costing'
                    ? 'border-blue-600 text-black'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-black'
                }`}
              >
                <span>Introduction to Costing & Management Accounting</span>
              </button>
            </nav>
          </div>

          <div className="p-6">
            {activeCourse === 'business' && <DesignABusiness userRole={userRole} />}
            {activeCourse === 'costing' && <IntroToCostingAndManAcc userRole={userRole} />}
          </div>
        </div>
      </div>
    </div>
  );
};

const ClientDashboard = () => {
  const { currentUser, logout } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedSection, setSelectedSection] = useState('dashboard');
  const [inboxSubsection, setInboxSubsection] = useState('public');
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [clientData, setClientData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchClientData = async () => {
      if (!currentUser?.uid) {
        setLoading(false);
        return;
      }

      try {
        // Check if user is an employee - use parent client ID
        const employeeUserData = localStorage.getItem('employeeUser');
        let effectiveUid = currentUser.uid;

        if (employeeUserData) {
          try {
            const parsed = JSON.parse(employeeUserData);
            effectiveUid = parsed.clientId || currentUser.uid;
          } catch (e) {
            // Invalid JSON, use current user UID
          }
        }

        const clientDocRef = doc(firestore, 'clients', effectiveUid);
        const clientDocSnap = await getDoc(clientDocRef);

        if (clientDocSnap.exists()) {
          const data = clientDocSnap.data();
          setClientData(data);
        } else {
          setClientData(null);
        }
      } catch (error) {
        setClientData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchClientData();
  }, [currentUser?.uid]);

  const confirmLogout = async () => {
    setShowLogoutConfirm(false);
    await logout();
  };

  // For employees: company (client) uid; for main client: own uid
  const effectiveClientUid = () => {
    if (!currentUser?.uid) return null;
    try {
      const employeeUserData = localStorage.getItem('employeeUser');
      if (employeeUserData) {
        const parsed = JSON.parse(employeeUserData);
        return parsed.clientId || currentUser.uid;
      }
    } catch (e) {}
    return currentUser.uid;
  };

  const sectionPlaceholders = {
    dashboard: (
      <div>
        <ClientsCRM
          assigned={clientData?.assigned}
          assignedEmail={clientData?.assignedEmail}
          onNavigateToTraining={() => setSelectedSection('training')}
        />
      </div>
    ),
    training: <ClientTraining />,
    'product-development': <ProductDevelopment userRole="client" />,
    scalingmodules: <ScalingModules userRole="client" />,
    commercialisation: <Commercialisation userRole="client" />,
    'commercialisation-readiness-checklist': (
      <CommercialisationReadinessChecklist userRole="client" />
    ),
    'go-to-market-strategy': <GTMStrategyPlan userRole="client" />,
    'startup-launch-checklist': <StartupLaunchChecklist userRole="client" />,
    funding: (
      <div className="mx-auto max-w-5xl space-y-8 p-4">
        <GrantBasics userRole="client" />
        <ApplicationTutorials userRole="client" />
      </div>
    ),

    settings: <SettingsPageComponent />,
    inbox: <PublicChat />,
    entrepreneur: <EntrepreneurDashboard />,
    profiling: <EntrepreneurProfilingDashboard userRole="client" userId={currentUser?.uid} />,
    'business-health-check': <HealthDashboard userRole="client" />,
    'document-bank': <DocumentBankPage />,
    'team-employees': (
      <div className="min-h-screen bg-gray-50 p-4 md:p-6">
        <div className="mx-auto max-w-7xl">
          <div className="mb-8">
            <h1 className="mb-2 text-3xl font-bold text-gray-900">Team & Employees</h1>
            <p className="text-gray-600">
              Add employees, assign roles (including founders), and set their access to training
              modules and assessments.
            </p>
          </div>
          <TeamMembersManagerV2 userId={effectiveClientUid()} isReadOnly={false} />
        </div>
      </div>
    ),
    'coaching-tools': <CoachingTools />,
    'best-practices': <BestPractices />,
    'knowledge-base': <KnowledgeBase />,
    templates: <Templates />,
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-white">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
          <p className="text-sm text-gray-500">Loading your dashboard...</p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex h-screen bg-white">
      <ClientSidebar
        collapsed={sidebarCollapsed}
        setCollapsed={setSidebarCollapsed}
        selected={selectedSection}
        setSelected={setSelectedSection}
        inboxSubsection={inboxSubsection}
        onInboxSubsectionChange={setInboxSubsection}
        user={currentUser}
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <ClientHeader user={currentUser} />
        <main className="flex-1 overflow-y-auto bg-white">
          {selectedSection === 'inbox' ? (
            inboxSubsection === 'public' ? (
              <PublicChat />
            ) : (
              <Mailbox />
            )
          ) : (
            sectionPlaceholders[selectedSection]
          )}
        </main>
      </div>
      <LogoutConfirmation
        isOpen={showLogoutConfirm}
        onConfirm={confirmLogout}
        onCancel={() => setShowLogoutConfirm(false)}
      />
    </div>
  );
};

export default ClientDashboard;
